import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCode,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiToolTip,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import {
  AnketaResources,
  AnketaResourcesEnum,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../../can";
import { useToast } from "src/notifications";
import { FormSkeleton } from "src/components/skeleton";
import {
  VpcResource,
  VpcResourceAction,
  VpcResourceInventory,
  VpcResourceSection,
  VpcResourceStatus,
} from "src/questionnaires";

const i18n = "anketa.opsanio.appliance.flyout.resources";

/**
 * How each state is drawn. `unknown` and `skipped` deliberately do NOT read as
 * failures: an unprobeable resource (OTOBO has no search operation) and a
 * resource that legitimately does not exist for this VPC (200-k3-server on a
 * CAPI-managed stage) are both correct states, not gaps.
 */
const STATUS_STYLE: Record<
  VpcResourceStatus,
  { icon: string; color: "success" | "danger" | "warning" | "subdued"; badge: string }
> = {
  present: { icon: "checkInCircleFilled", color: "success", badge: "success" },
  missing: { icon: "crossInCircleFilled", color: "danger", badge: "danger" },
  partial: { icon: "alert", color: "warning", badge: "warning" },
  unknown: { icon: "questionInCircle", color: "subdued", badge: "hollow" },
  skipped: { icon: "minusInCircle", color: "subdued", badge: "hollow" },
  error: { icon: "warning", color: "warning", badge: "warning" },
};

/** Section headings, in the order an operator walks the bootstrap. */
const SECTION_ORDER: VpcResourceSection[] = [
  "gitlab",
  "vault",
  "keycloak",
  "docs",
  "netbox",
  "prtg",
  "otobo",
  "pangolin",
];

const SECTION_META: Record<
  VpcResourceSection,
  { icon: string; defaultLabel: string }
> = {
  gitlab: { icon: "logoGitlab", defaultLabel: "GitLab" },
  vault: { icon: "lock", defaultLabel: "Vault" },
  keycloak: { icon: "users", defaultLabel: "Keycloak" },
  docs: { icon: "documentation", defaultLabel: "Documentation & tokens" },
  netbox: { icon: "storage", defaultLabel: "NetBox" },
  prtg: { icon: "monitoringApp", defaultLabel: "PRTG" },
  otobo: { icon: "email", defaultLabel: "OTOBO" },
  pangolin: { icon: "globe", defaultLabel: "Pangolin" },
};

/** Statuses that mean "an operator still has work to do here". */
const isGap = (status: VpcResourceStatus): boolean =>
  status === "missing" || status === "partial" || status === "error";

/**
 * Provisioning inventory for one appliance: every resource anketa creates,
 * whether it exists, and the action that creates the missing ones.
 *
 * The rows are produced server-side (functions/gitlab/onboarding/inventory.ts) -
 * this view only renders them and posts the action each row names. Two things
 * that follow from that and are load-bearing here:
 *
 *  - **The action endpoint is the boundary.** `action.permission` only decides
 *    whether the button is drawn; the route re-checks it. A row whose action
 *    lands on the PRTG or OTOBO endpoint keeps that endpoint's own permission,
 *    so this tab can never be used to widen `opsanio/bootstrap` into them.
 *  - **`unknown` is not `missing`.** Rows the owning system cannot answer for
 *    are drawn neutrally and excluded from the gap count.
 */
export const OpsanioApplianceFlyoutResources = ({
  appliance,
  onOpenPangolin,
  onOpenHardware,
}: {
  appliance: OpsanioApplianceData;
  /** Pangolin's per-app resources are created on its own tab, not here. */
  onOpenPangolin?: () => void;
  /**
   * NetBox drift is repaired from the Hardware tab, which previews the plan
   * before it writes. This tab deliberately does not offer a one-click apply -
   * skipping that preview would be a step backwards.
   */
  onOpenHardware?: () => void;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const can = useOpsanioCan();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [inventory, setInventory] = useState<VpcResourceInventory | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  /** Key of the row whose action is currently running. */
  const [running, setRunning] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () =>
      typeof keycloak?.token === "string" &&
      !!appliance.catalogId &&
      !!appliance.id,
    [keycloak, appliance.catalogId, appliance.id],
  );

  const load = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    const response = await Get<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/resources/${appliance.catalogId}/${appliance.id}`,
      responseType: "json",
      token: keycloak.token,
    });
    setLoading(false);
    // The api client never throws on an HTTP error - it reports it on the
    // envelope, so this check is the only error path that exists.
    if (!response.success || !response.data?.data) {
      setInventory(null);
      setError(
        response.error?.message ??
          translate(`${i18n}.error`, {
            defaultMessage: "Could not read the provisioning inventory.",
          }),
      );
      return;
    }
    setInventory(response.data.data as VpcResourceInventory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, appliance.catalogId, appliance.id, keycloak, translate]);

  useEffect(() => {
    load();
  }, [load]);

  /** May the current user run this action? Affordance only - the route re-checks. */
  const mayRun = useCallback(
    (action: VpcResourceAction): boolean => {
      const resource = action.permission.resource as AnketaResources;
      // The infra resources are route-narrowed to internal orgs, and the PRTG
      // and OTOBO write routes additionally require opsanio/appliance · create.
      if (!can(resource, action.permission.action, { isInternal: true })) {
        return false;
      }
      return resource === AnketaResourcesEnum.opsanioPrtg ||
        resource === AnketaResourcesEnum.opsanioOtobo
        ? can(AnketaResourcesEnum.opsanioAppliance, "create")
        : true;
    },
    [can],
  );

  const run = useCallback(
    async (resource: VpcResource) => {
      const action = resource.action;
      if (!action || !canFetch) return;
      setRunning(resource.key);
      const response = await Post<any>({
        path: `v2/${action.path}`,
        value: { questionnaireId: appliance.id },
        responseType: "json",
        token: keycloak.token,
      });
      setRunning(null);

      // The bootstrap route answers with an OnboardResult (per-step), the narrow
      // actions with { ok, message }. Both are wrapped as { data: … }.
      const payload = response.data?.data;
      const ok =
        response.success &&
        (payload == null || payload.ok === undefined || payload.ok === true);

      addToast(
        ok
          ? {
              color: "success",
              iconType: "checkInCircleFilled",
              title: resource.label,
              text:
                payload?.message ??
                translate(`${i18n}.action.success`, {
                  defaultMessage: "Resource provisioned.",
                }),
            }
          : {
              color: "warning",
              iconType: "alert",
              title: resource.label,
              text:
                payload?.message ??
                response.error?.message ??
                translate(`${i18n}.action.failed`, {
                  defaultMessage:
                    "The action finished with issues. Re-read the inventory for the current state.",
                }),
            },
      );

      await load();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canFetch, appliance.id, keycloak, addToast, translate, load],
  );

  const gaps = useMemo<number>(
    () => (inventory?.resources ?? []).filter((r) => isGap(r.status)).length,
    [inventory],
  );

  const header = (
    <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiIcon type="inspect" size="l" />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiTitle size="s">
          <h3 style={{ margin: 0 }}>
            {translate(`${i18n}.title`, { defaultMessage: "Provisioned resources" })}
          </h3>
        </EuiTitle>
      </EuiFlexItem>
      {inventory && (
        <EuiFlexItem grow={false}>
          <EuiBadge color={gaps === 0 ? "success" : "danger"}>
            {gaps === 0
              ? translate(`${i18n}.complete`, { defaultMessage: "Complete" })
              : translate(`${i18n}.gaps`, {
                  defaultMessage: "{{count}} missing",
                  values: { count: gaps },
                })}
          </EuiBadge>
        </EuiFlexItem>
      )}
      {inventory?.manageViaCapi && (
        <EuiFlexItem grow={false}>
          <EuiBadge color="hollow">CAPI</EuiBadge>
        </EuiFlexItem>
      )}
      <EuiFlexItem />
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="refresh"
          onClick={load}
          isLoading={loading}
          disabled={!canFetch}
        >
          {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const wrap = (body: React.ReactNode) => (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      {header}
      <EuiHorizontalRule margin="m" />
      {body}
    </EuiPanel>
  );

  if (!canFetch) {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="help"
        title={translate(`${i18n}.noQuestionnaire.title`, {
          defaultMessage: "No questionnaire behind this appliance",
        })}
      />,
    );
  }

  if (loading && inventory === null) return wrap(<FormSkeleton />);

  if (error && inventory === null) {
    return wrap(<EuiCallOut color="danger" iconType="alert" title={error} />);
  }

  if (!inventory) return wrap(<></>);

  const renderRow = (resource: VpcResource): JSX.Element => {
    const style = STATUS_STYLE[resource.status] ?? STATUS_STYLE.unknown;
    const action = resource.action;
    // Two sections are repaired on their own tab rather than here: Pangolin,
    // which owns the per-app target templates, and NetBox, whose sync previews
    // the plan before writing. Linking beats duplicating either flow.
    const deepLink = isGap(resource.status)
      ? resource.section === "pangolin"
        ? onOpenPangolin
        : resource.section === "netbox"
          ? onOpenHardware
          : undefined
      : undefined;

    return (
      <React.Fragment key={resource.key}>
        <EuiFlexGroup
          alignItems="flexStart"
          gutterSize="s"
          responsive={false}
          style={{ marginBottom: 4 }}
        >
          <EuiFlexItem grow={false}>
            <EuiIcon
              type={style.icon}
              color={style.color}
              style={{ marginTop: 3 }}
            />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">
              <strong>{resource.label}</strong>{" "}
              <EuiBadge color={style.badge}>
                {translate(`${i18n}.status.${resource.status}`, {
                  defaultMessage: resource.status,
                })}
              </EuiBadge>
            </EuiText>
            {resource.path && (
              <EuiText size="xs" color="subdued">
                {resource.href ? (
                  <EuiLink href={resource.href} target="_blank" external>
                    <EuiCode transparentBackground>{resource.path}</EuiCode>
                  </EuiLink>
                ) : (
                  <EuiCode transparentBackground>{resource.path}</EuiCode>
                )}
              </EuiText>
            )}
            {resource.detail && (
              <EuiText size="xs" color="subdued">
                {resource.detail}
              </EuiText>
            )}
            {resource.hint && (
              <EuiText size="xs" color="warning">
                {resource.hint}
              </EuiText>
            )}
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            {deepLink ? (
              <EuiButtonEmpty size="xs" iconType="popout" onClick={deepLink}>
                {resource.section === "netbox"
                  ? translate(`${i18n}.openHardware`, {
                      defaultMessage: "Open Hardware tab",
                    })
                  : translate(`${i18n}.openPangolin`, {
                      defaultMessage: "Open Pangolin tab",
                    })}
              </EuiButtonEmpty>
            ) : action && mayRun(action) ? (
              <EuiToolTip
                content={
                  action.runsFullBootstrap
                    ? translate(`${i18n}.action.fullBootstrapHint`, {
                        defaultMessage:
                          "This resource is created by the orchestrated bootstrap run, which also (re)creates every other resource it owns. It is idempotent - existing resources are left as they are.",
                      })
                    : translate(`${i18n}.action.narrowHint`, {
                        defaultMessage:
                          "Creates this resource only. Idempotent - safe to run again.",
                      })
                }
              >
                <EuiButton
                  size="s"
                  iconType={action.runsFullBootstrap ? "play" : "plusInCircle"}
                  fill={!action.runsFullBootstrap}
                  isLoading={running === resource.key}
                  disabled={running !== null}
                  onClick={() => run(resource)}
                >
                  {translate(`${i18n}.action.${action.key}`, {
                    defaultMessage: action.label,
                  })}
                </EuiButton>
              </EuiToolTip>
            ) : null}
          </EuiFlexItem>
        </EuiFlexGroup>
      </React.Fragment>
    );
  };

  return wrap(
    <>
      {error && (
        <>
          <EuiCallOut color="warning" iconType="alert" title={error} />
          <EuiSpacer size="m" />
        </>
      )}

      {inventory.readiness.length > 0 && (
        <>
          <EuiCallOut
            color="warning"
            iconType="alert"
            title={translate(`${i18n}.readiness.title`, {
              defaultMessage:
                "The questionnaire is not ready - the bootstrap will refuse to run",
            })}
          >
            <EuiText size="s">
              <ul style={{ marginBottom: 0 }}>
                {inventory.readiness.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </EuiText>
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}

      {SECTION_ORDER.map((section) => {
        const rows = inventory.resources.filter((r) => r.section === section);
        if (rows.length === 0) return null;
        const meta = SECTION_META[section];
        return (
          <React.Fragment key={section}>
            <EuiFlexGroup
              alignItems="center"
              gutterSize="xs"
              responsive={false}
              style={{ marginBottom: 6 }}
            >
              <EuiFlexItem grow={false}>
                <EuiIcon type={meta.icon} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiText size="s">
                  <strong>
                    {translate(`${i18n}.section.${section}`, {
                      defaultMessage: meta.defaultLabel,
                    })}
                  </strong>
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
            {rows.map(renderRow)}
            <EuiSpacer size="m" />
          </React.Fragment>
        );
      })}

      <EuiHorizontalRule margin="s" />
      <EuiText size="xs" color="subdued">
        {translate(`${i18n}.footer`, {
          defaultMessage:
            "Presence only - no secret value is ever read out of Vault or GitLab. Read at",
        })}{" "}
        {new Date(inventory.fetchedAt).toLocaleString()}
      </EuiText>
    </>,
  );
};

export default OpsanioApplianceFlyoutResources;
